import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

/** Recalculate positions of panels based on frame positions */
function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')
  const leftPanel = getParam(params, 'left_glass_panel')
  const topCenterPanel = getParam(params, 'top_center_glass_panel')
  const centerDoorPanel = getParam(params, 'center_door_glass_panel')
  const rightPanel = getParam(params, 'right_glass_panel')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width

  if (leftPanel) {
    const newHeight = leftFrame.height - frameThickness - frameThickness
    updateParam(model, 'left_glass_panel', {
      position: { x: leftFrame.width, y: frameThickness, z: leftPanel.position?.z || 0 },
      height: newHeight,
    })
    currentX += leftPanel.width
  }

  if (topCenterPanel) {
    updateParam(model, 'top_center_glass_panel', {
      position: { x: currentX, y: frameThickness, z: topCenterPanel.position?.z || 0 },
    })
  }

  if (centerDoorPanel && topCenterPanel) {
    const transomHeight = topFrame.height + topCenterPanel.height
    updateParam(model, 'center_door_glass_panel', {
      position: { x: currentX, y: transomHeight, z: centerDoorPanel.position?.z || 0 },
    })
  }

  if (topCenterPanel && rightPanel) {
    currentX += topCenterPanel.width
    const rightFrame = getParam(model.parameters!, 'right_frame_profile')
    const newHeight = rightFrame
      ? rightFrame.height - frameThickness - frameThickness
      : rightPanel.height
    updateParam(model, 'right_glass_panel', {
      position: { x: currentX, y: frameThickness, z: rightPanel.position?.z || 0 },
      height: newHeight,
    })
  }
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const currentTotalWidth = model.width as number
  const widthDifference = numValue - currentTotalWidth
  const halfDifference = widthDifference / 2

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (cascadeUpdates) {
    const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
    const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
    const leftFrame = getParam(model.parameters!, 'left_frame_profile')
    const rightFrame = getParam(model.parameters!, 'right_frame_profile')
    const doorPanel = getParam(model.parameters!, 'center_door_glass_panel')
    const leftPanel = getParam(model.parameters!, 'left_glass_panel')
    const rightPanel = getParam(model.parameters!, 'right_glass_panel')

    const doorWidth = doorPanel?.width || 900

    if (bottomLeft && leftFrame) {
      const newLeftSectionWidth = bottomLeft.width + halfDifference
      updateParam(model, 'bottom_left_frame_profile', { width: newLeftSectionWidth })
      if (leftPanel) {
        updateParam(model, 'left_glass_panel', { width: newLeftSectionWidth - leftFrame.width })
      }
    }

    if (bottomRight && rightFrame) {
      const newRightSectionWidth = bottomRight.width + halfDifference
      updateParam(model, 'bottom_right_frame_profile', { width: newRightSectionWidth })
      if (rightPanel) {
        updateParam(model, 'right_glass_panel', { width: newRightSectionWidth - rightFrame.width })
      }
    }

    if (rightFrame && bottomLeft) {
      const newLeftSectionWidth = bottomLeft.width + halfDifference
      const newRightFrameX = newLeftSectionWidth + doorWidth
      updateParam(model, 'right_frame_profile', {
        position: { ...rightFrame.position, x: newRightFrameX },
      })
      if (bottomRight) {
        const br = getParam(model.parameters!, 'bottom_right_frame_profile')
        if (br) {
          updateParam(model, 'bottom_right_frame_profile', {
            position: { ...br.position, x: newRightFrameX },
          })
        }
      }
    }

    recalculatePositions(model)
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  if (cascadeUpdates) {
    const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
    const topFrame = getParam(model.parameters!, 'top_frame_profile')

    if (topCenterPanel && topFrame) {
      const leftFrame = getParam(model.parameters!, 'left_frame_profile')
      const rightFrame = getParam(model.parameters!, 'right_frame_profile')
      const remainingDoorHeight = numValue - topCenterPanel.height - topFrame.height

      if (leftFrame) {
        updateParam(model, 'left_glass_panel', {
          height: leftFrame.height - frameThickness - frameThickness,
        })
      }
      if (rightFrame) {
        updateParam(model, 'right_glass_panel', {
          height: rightFrame.height - frameThickness - frameThickness,
        })
      }

      updateParam(model, 'center_door_glass_panel', { height: remainingDoorHeight })

      const doorPanel = getParam(model.parameters!, 'center_door_glass_panel')
      if (doorPanel?.hardware) {
        const updatedHardware = { ...doorPanel.hardware }

        if (updatedHardware.handles) {
          updatedHardware.handles = updatedHardware.handles.map((handle) => ({
            ...handle,
            position: { ...handle.position, y: Math.round(remainingDoorHeight * 0.47) },
          }))
        }
        if (updatedHardware.hinges) {
          updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
            if (hinge.position.y < 0) {
              return { ...hinge, position: { ...hinge.position, y: -glassHingeHeight / 2 } }
            }
            return { ...hinge, position: { ...hinge.position, y: remainingDoorHeight - glassHingeHeight } }
          })
        }
        if (updatedHardware.locks) {
          updatedHardware.locks = updatedHardware.locks.map((lock) => ({
            ...lock,
            position: { ...lock.position, y: remainingDoorHeight - glassLockHeight },
          }))
        }

        updateParam(model, 'center_door_glass_panel', { hardware: updatedHardware })
      }
    }
  }

  return model
}

const parameterWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId, cascadeUpdates = true } = options as { positionId?: string; cascadeUpdates?: boolean }
  if (!positionId) return model

  const currentParameter = getParam(model.parameters!, positionId as string)
  if (!currentParameter) return model

  const currentWidth = currentParameter.width
  const widthDifference = numValue - currentWidth
  updateParam(model, positionId as string, { width: numValue })

  if (cascadeUpdates) {
    if (positionId === 'center_door_glass_panel' || positionId === 'top_center_glass_panel') {
      const otherPanel = positionId === 'center_door_glass_panel'
        ? 'top_center_glass_panel'
        : 'center_door_glass_panel'
      updateParam(model, otherPanel, { width: numValue })
      model.doorConfiguration = {
        ...model.doorConfiguration!,
        totalDoorWidth: numValue,
        individualDoorWidth: [numValue],
      }
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })

      const doorPanel = getParam(model.parameters!, 'center_door_glass_panel')
      if (doorPanel?.hardware) {
        const updatedHardware = { ...doorPanel.hardware }
        if (updatedHardware.handles) {
          updatedHardware.handles = updatedHardware.handles.map((h) => ({
            ...h, position: { ...h.position, x: 50 },
          }))
        }
        if (updatedHardware.hinges) {
          updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
            if (hinge.positionId === 'door_2_right_top_hinge_1') {
              return { ...hinge, position: { ...hinge.position, x: numValue - glassHingeWidth / 2 } }
            }
            if (hinge.positionId === 'door_2_right_bottom_hinge_2') {
              return { ...hinge, position: { ...hinge.position, x: numValue - glassHingeWidth } }
            }
            return hinge
          })
        }
        if (updatedHardware.locks) {
          updatedHardware.locks = updatedHardware.locks.map((lock) => ({
            ...lock, position: { ...lock.position, x: glassLockWidth },
          }))
        }
        updateParam(model, 'center_door_glass_panel', { hardware: updatedHardware })
      }
    } else if (positionId === 'left_glass_panel') {
      const leftFrame = getParam(model.parameters!, 'left_frame_profile')
      if (leftFrame) {
        updateParam(model, 'bottom_left_frame_profile', { width: numValue + leftFrame.width })
      }
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })
    } else if (positionId === 'right_glass_panel') {
      const rightFrame = getParam(model.parameters!, 'right_frame_profile')
      if (rightFrame) {
        updateParam(model, 'bottom_right_frame_profile', { width: numValue + rightFrame.width })
      }
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })
    }

    recalculatePositions(model)
  }

  return model
}

const topCenterHeight: DimensionHandler = (model, _initial, numValue) => {
  const currentTopCenter = getParam(model.parameters!, 'top_center_glass_panel')
  const doorPanel = getParam(model.parameters!, 'center_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!currentTopCenter || !doorPanel || !topFrame) return model

  const newTopCenterHeight = numValue - topFrame.height
  const newTransomHeight = numValue

  updateParam(model, 'top_center_glass_panel', {
    height: newTopCenterHeight,
    position: { ...currentTopCenter.position, y: frameThickness },
  })

  updateParam(model, 'center_door_glass_panel', {
    position: { ...doorPanel.position, y: newTransomHeight },
  })

  const newTotalHeight = newTransomHeight + doorPanel.height
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  return model
}

const centerPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel = getParam(model.parameters!, 'center_door_glass_panel')
  const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!doorPanel || !topCenterPanel || !topFrame) return model

  updateParam(model, 'center_door_glass_panel', { height: numValue })

  const newTotalHeight = topFrame.height + topCenterPanel.height + numValue
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  if (doorPanel.hardware) {
    const updatedHardware = { ...doorPanel.hardware }
    if (updatedHardware.handles) {
      updatedHardware.handles = updatedHardware.handles.map((h) => ({
        ...h, position: { ...h.position, y: Math.round(numValue * 0.47) },
      }))
    }
    if (updatedHardware.hinges) {
      updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
        if (hinge.position.y < 0) {
          return { ...hinge, position: { ...hinge.position, y: -glassHingeHeight / 2 } }
        }
        return { ...hinge, position: { ...hinge.position, y: numValue - glassHingeHeight } }
      })
    }
    if (updatedHardware.locks) {
      updatedHardware.locks = updatedHardware.locks.map((lock) => ({
        ...lock, position: { ...lock.position, y: numValue - glassLockHeight },
      }))
    }
    updateParam(model, 'center_door_glass_panel', { hardware: updatedHardware })
  }

  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')

  if (!bottomLeft || !leftFrame) return model

  const widthDifference = numValue - bottomLeft.width
  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  updateParam(model, 'left_glass_panel', { width: numValue - leftFrame.width })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  recalculatePositions(model)
  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  if (!bottomRight || !rightFrame) return model

  const widthDifference = numValue - bottomRight.width
  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  updateParam(model, 'right_glass_panel', { width: numValue - rightFrame.width })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const leftSectionWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 400
  const doorWidth = getParam(model.parameters!, 'center_door_glass_panel')?.width || 900
  const newRightFrameX = leftSectionWidth + doorWidth
  updateParam(model, 'right_frame_profile', {
    position: { ...rightFrame.position, x: newRightFrameX },
  })
  const br = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (br) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...br.position, x: newRightFrameX },
    })
  }

  recalculatePositions(model)
  return model
}

const handleHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId } = options as { positionId: string }
  const param = getParam(model.parameters!, positionId)
  if (!param?.hardware?.handles?.[0]) return model

  updateParam(model, positionId, {
    hardware: {
      ...param.hardware,
      handles: [
        {
          ...param.hardware.handles[0],
          position: { ...param.hardware.handles[0].position, y: numValue },
        },
      ],
    },
  })

  return model
}

export const model1DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  parameterWidth,
  topCenterHeight,
  centerPanelHeight,
  leftFrameWidth,
  rightFrameWidth,
  handleHeight,
}
